import { StringDevUIFieldDescriptor as UIFieldDescriptor } from "../../core/ui/StringDevUISchema";
import { GridAdapter } from "./GridAdapter";

/**
 * Row grid adapter.
 * Renders horizontal rows by count or by size.
 * Useful for baseline grids and vertical rhythm verification.
 */
export class RowsAdapter extends GridAdapter {
  readonly type = "rows";
  readonly label = "Rows";
  readonly icon = `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="10" height="2" fill="currentColor"/><rect x="3" y="7" width="10" height="2" fill="currentColor"/><rect x="3" y="11" width="10" height="2" fill="currentColor"/></svg>`;

  private static readonly UNIT_OPTIONS = [
    { value: "px", label: "px" },
    { value: "%", label: "%" },
    { value: "vh", label: "vh" },
    { value: "vw", label: "vw" },
    { value: "em", label: "em" },
    { value: "rem", label: "rem" },
  ] as const;

  getDefaults(): Record<string, any> {
    return {
      mode: "count",
      count: 8,
      size: 24,
      sizeUnit: "px",
      gap: 0,
      gapUnit: "px",
      margin: 0,
      marginUnit: "px",
      color: "#e2784a",
      opacity: 0.2,
      style: "line",
    };
  }

  getUISchema(): UIFieldDescriptor[] {
    return [
      {
        type: "select",
        key: "mode",
        label: "Mode",
        default: "count",
        options: [
          { value: "count", label: "By count" },
          { value: "size", label: "By size" },
        ],
      },
      {
        type: "number",
        key: "count",
        label: "Rows",
        default: 8,
        min: 1,
        max: 48,
        step: 1,
        disabledWhen: { key: "mode", equals: "size" },
      },
      {
        type: "range",
        key: "size",
        label: "Size",
        default: 24,
        min: 0,
        max: 1200,
        step: 1,
        units: RowsAdapter.UNIT_OPTIONS,
        defaultUnit: "px",
        disabledWhen: { key: "mode", equals: "count" },
      },
      {
        type: "range",
        key: "gap",
        label: "Gap",
        default: 0,
        min: 0,
        max: 100,
        step: 1,
        units: RowsAdapter.UNIT_OPTIONS,
        defaultUnit: "px",
      },
      {
        type: "range",
        key: "margin",
        label: "Margin",
        default: 0,
        min: 0,
        max: 100,
        step: 1,
        units: RowsAdapter.UNIT_OPTIONS,
        defaultUnit: "px",
      },
      { type: "divider" },
      { type: "color", key: "color", label: "Color", default: "#e2784a" },
      {
        type: "range",
        key: "opacity",
        label: "Opacity",
        default: 0.2,
        min: 0,
        max: 1,
        step: 0.01,
        displayMultiplier: 100,
        displayStep: 10,
      },
      {
        type: "select",
        key: "style",
        label: "Style",
        default: "line",
        options: [
          { value: "line", label: "Lines" },
          { value: "fill", label: "Fill" },
        ],
      },
    ];
  }

  render(svg: SVGSVGElement, width: number, height: number, settings: Record<string, any>): void {
    const group = this.getGroup(svg, settings.__instanceId);
    const {
      mode,
      count,
      size: rawSize,
      sizeUnit,
      gap: rawGap,
      gapUnit,
      margin: rawMargin,
      marginUnit,
      color,
      opacity,
      style,
    } = settings;

    const referenceElement = svg.parentElement;
    const gap = this.resolveUnit(rawGap, gapUnit || "px", height, referenceElement);
    const margin = this.resolveUnit(rawMargin, marginUnit || "px", height, referenceElement);
    const availableHeight = height - margin * 2;

    if (availableHeight <= 0) return;

    if (mode === "count") {
      this.renderByCount(group, width, availableHeight, margin, count, gap, color, opacity, style);
    } else {
      const size = this.resolveUnit(rawSize, sizeUnit || "px", height, referenceElement);
      if (size <= 0) return;
      this.renderBySize(group, width, availableHeight, margin, size, gap, color, opacity, style);
    }
  }

  private renderByCount(
    group: SVGGElement,
    width: number,
    available: number,
    margin: number,
    count: number,
    gap: number,
    color: string,
    opacity: number,
    style: string,
  ): void {
    if (style === "fill") {
      const bandHeight = (available - (count - 1) * gap) / count;
      if (bandHeight <= 0) return;
      for (let i = 0; i < count; i++) {
        const y = margin + i * (bandHeight + gap);
        group.appendChild(this.createRect(0, y, width, bandHeight, color, opacity));
      }
    } else {
      if (count === 1) {
        const y = margin + available / 2;
        group.appendChild(this.createLine(0, y, width, y, color, opacity));
        return;
      }
      const step = available / (count - 1);
      for (let i = 0; i < count; i++) {
        const y = margin + i * step;
        group.appendChild(this.createLine(0, y, width, y, color, opacity));
      }
    }
  }

  private renderBySize(
    group: SVGGElement,
    width: number,
    available: number,
    margin: number,
    size: number,
    gap: number,
    color: string,
    opacity: number,
    style: string,
  ): void {
    if (style === "fill") {
      const step = size + gap;
      for (let y = 0; y < available; y += step) {
        const h = Math.min(size, available - y);
        group.appendChild(this.createRect(0, margin + y, width, h, color, opacity));
      }
    } else {
      for (let y = 0; y <= available; y += size) {
        group.appendChild(this.createLine(0, margin + y, width, margin + y, color, opacity));
      }
    }
  }
}
